import React, { Fragment } from 'react';
import { get } from 'lodash-es';
import { Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const Step2TableColumns = (
  handleModal,
  editLot,
  handleCheckbox,
  handleCheckboxAll,
  selectedLots,
  spreadsheetErrors
) => {
  // The following items will be in both live and timed sales
  const tableColumns = [
    {
      Header: () => (
        <Fragment>
          {selectedLots.size > 0 ? (
            <FontAwesomeIcon
              className="color-blue cursor-pointer"
              icon="minus-square"
              onClick={() => handleCheckboxAll()}
            />
          ) : (
            <Input
              className="ml-0 position-relative"
              checked={false}
              onChange={() => handleCheckboxAll()}
              type="checkbox"
            />
          )}
        </Fragment>
      ),
      headerClassName: 'd-flex justify-content-center align-items-center',
      id: 'selectIcon',
      fixed: 'left',
      width: 55,
      sortable: false,
      accessor: d => ({
        id: d.id
      }),
      Cell: ({ value: { id } }) => (
        <div className="text-center">
          <Input
            className="ml-0 position-relative"
            checked={selectedLots.has(id)}
            onChange={() => handleCheckbox(id)}
            type="checkbox"
          />
        </div>
      )
    },
    {
      Header: 'Actions',
      id: 'actions',
      fixed: 'left',
      width: 70,
      sortable: false,
      accessor: d => ({
        id: d.id
      }),
      Cell: ({ value: { id } }) => (
        <div className="px-2 text-center">
          <FontAwesomeIcon
            className="color-blue cursor-pointer"
            icon="edit"
            onClick={() => editLot(id)}
          />
          <FontAwesomeIcon
            className="color-blue cursor-pointer ml-2"
            icon="trash-alt"
            onClick={() => handleModal('deleteLotModal', true, null, id)}
          />
        </div>
      )
    },
    {
      Header: 'Item #*',
      id: 'lotNumber',
      width: 70,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotNum = get(rowInfo, 'original.lotNumber');
        const lotId = lotNum + get(rowInfo, 'original.lotExt');
        const lotError = !lotNum
          || get(spreadsheetErrors, '[108].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[109].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[110].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[111].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[112].lotNumbers', new Set()).has(lotId);
        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        lotNumber: d.lotNumber
      }),
      Cell: ({ value: { lotNumber } }) => <p>{lotNumber}</p>
    },
    {
      Header: 'Lot Ext.',
      id: 'lotExt',
      width: 80,
      sortable: false,
      accessor: d => ({
        lotExt: d.lotExt
      }),
      Cell: ({ value: { lotExt } }) => <p>{lotExt}</p>
    },
    {
      Header: 'Title*',
      id: 'title',
      width: 260,
      sortable: false,
      className: 'wrap-table-cell',
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotError = get(spreadsheetErrors, '[101].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[165].lotNumbers', new Set()).has(lotId);
        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        lotTitle: d.lotTitle
      }),
      Cell: ({ value: { lotTitle } }) => (
        <p>{lotTitle.length > 50 ? `${lotTitle.substring(0, 50)}...` : lotTitle}</p>
      )
    },
    {
      Header: 'Description*',
      id: 'description',
      width: 260,
      sortable: false,
      className: 'wrap-table-cell',
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotError = get(spreadsheetErrors, '[164].lotNumbers', new Set()).has(lotId);
        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        lotDescription: d.lotDescription
      }),
      Cell: ({ value: { lotDescription } }) => (
        <p>
          {lotDescription.length > 50 ? `${lotDescription.substring(0, 50)}...` : lotDescription}
        </p>
      )
    },
    {
      Header: 'Price',
      id: 'price',
      width: 100,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotError = get(spreadsheetErrors, '[118].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[119].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[142-startingBid].lotNumbers', new Set()).has(lotId);
        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        startingBid: d.startingBid
      }),
      Cell: ({ value: { startingBid } }) => <p>{startingBid}</p>
    },
    {
      Header: 'Item Dimensions',
      id: 'dimensions',
      width: 140,
      accessor: d => ({
        dimensions: d.dimensions
      }),
      Cell: ({ value: { dimensions } }) => <p>{dimensions}</p>
    },
    {
      Header: 'Shipping Length',
      id: 'shipLength',
      width: 140,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotError = get(spreadsheetErrors, '[172].lotNumbers', new Set()).has(lotId);
        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        shipLength: d.shipLength
      }),
      Cell: ({ value: { shipLength } }) => <p>{shipLength}</p>
    },
    {
      Header: 'Shipping Height',
      id: 'shipHeight',
      width: 140,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotError = get(spreadsheetErrors, '[170].lotNumbers', new Set()).has(lotId);
        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        shipHeight: d.shipHeight
      }),
      Cell: ({ value: { shipHeight } }) => <p>{shipHeight}</p>
    },
    {
      Header: 'Shipping Width',
      id: 'shipWidth',
      width: 140,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotError = get(spreadsheetErrors, '[173].lotNumbers', new Set()).has(lotId);
        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        shipWidth: d.shipWidth
      }),
      Cell: ({ value: { shipWidth } }) => <p>{shipWidth}</p>
    },
    {
      Header: 'Total Weight',
      id: 'shipWeight',
      width: 140,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotError = get(spreadsheetErrors, '[171].lotNumbers', new Set()).has(lotId);
        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        shipWeight: d.shipWeight
      }),
      Cell: ({ value: { shipWeight } }) => <p>{shipWeight}</p>
    },
    {
      Header: 'Inventory #',
      id: 'inventoryNumber',
      width: 100,
      getProps: (state, rowInfo) => {
        const hasError = get(rowInfo, 'original.hasError');
        if (!hasError) {
          return { className: '' };
        }

        const lotId = get(rowInfo, 'original.lotNumber') + get(rowInfo, 'original.lotExt');
        const lotError = get(spreadsheetErrors, '[120].lotNumbers', new Set()).has(lotId)
          || get(spreadsheetErrors, '[121].lotNumbers', new Set()).has(lotId);
        return {
          className: lotError ? 'no-image-border' : ''
        };
      },
      accessor: d => ({
        invNumber: d.invNumber
      }),
      Cell: ({ value: { invNumber } }) => <p>{invNumber}</p>
    },
    {
      Header: 'Shipping (Domestic)',
      id: 'shipping',
      width: 260,
      accessor: d => ({
        shipping: d.shipping
      }),
      Cell: ({ value: { shipping } }) => <p>{shipping}</p>
    },
    {
      Header: 'Shipping (International)',
      id: 'shippingIntl',
      width: 260,
      accessor: d => ({
        shippingIntl: d.shippingIntl
      }),
      Cell: ({ value: { shippingIntl } }) => <p>{shippingIntl}</p>
    },
    {
      Header: 'Condition',
      id: 'condition',
      width: 260,
      sortable: false,
      className: 'wrap-table-cell',
      accessor: d => ({
        condition: d.condition
      }),
      Cell: ({ value: { condition } }) => (
        <p>{condition.length > 50 ? `${condition.substring(0, 50)}...` : condition}</p>
      )
    },
    {
      Header: 'Other Notes',
      id: 'notes',
      width: 260,
      accessor: d => ({
        notes: d.notes
      }),
      Cell: ({ value: { notes } }) => (
        <p>{notes.length > 50 ? `${notes.substring(0, 50)}...` : notes}</p>
      )
    },
    {
      Header: 'Provenance',
      id: 'provenance',
      width: 100,
      accessor: d => ({
        provenance: d.provenance
      }),
      Cell: ({ value: { provenance } }) => <p>{provenance}</p>
    },
    {
      Header: 'Artist Name',
      id: 'artistName',
      width: 100,
      accessor: d => ({
        artistName: d.artistName
      }),
      Cell: ({ value: { artistName } }) => <p>{artistName}</p>
    },
    {
      Header: 'Exhibited',
      id: 'exhibited',
      width: 260,
      sortable: false,
      className: 'wrap-table-cell',
      accessor: d => ({
        exhibited: d.exhibited
      }),
      Cell: ({ value: { exhibited } }) => (
        <p>{exhibited.length > 50 ? `${exhibited.substring(0, 50)}...` : exhibited}</p>
      )
    },
    {
      Header: 'Literature',
      id: 'literature',
      width: 260,
      sortable: false,
      className: 'wrap-table-cell',
      accessor: d => ({
        literature: d.literature
      }),
      Cell: ({ value: { literature } }) => (
        <p>{literature.length > 50 ? `${literature.substring(0, 50)}...` : literature}</p>
      )
    },
    {
      Header: 'Medium',
      id: 'medium',
      width: 100,
      accessor: d => ({
        medium: d.medium
      }),
      Cell: ({ value: { medium } }) => <p>{medium}</p>
    },
    {
      Header: 'Circa',
      id: 'circa',
      width: 100,
      accessor: d => ({
        circa: d.circa
      }),
      Cell: ({ value: { circa } }) => <p>{circa}</p>
    }
  ];

  return tableColumns;
};

export default Step2TableColumns;
